import React, { useEffect, useState, useCallback } from 'react';
import {
  Row, Col, Card, Statistic, Table, Tag, Button, DatePicker,
  Spin, Alert, Tooltip, Segmented, Typography, Space,
} from 'antd';
import {
  DollarOutlined, TeamOutlined,
  CrownOutlined, ReloadOutlined, FileTextOutlined, CalendarOutlined,
} from '@ant-design/icons';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid,
  Tooltip as RTooltip, ResponsiveContainer, BarChart, Bar, Legend,
} from 'recharts';
import { theme as antdTheme } from 'antd';
import dayjs from 'dayjs';
import axios from 'axios';

const { Text } = Typography;
const { RangePicker } = DatePicker;
const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const PRO_PRICE = 9.99;   // cents → dollars handled inline

const fmt$ = (cents) => {
  const d = (cents || 0) / 100;
  return d >= 1000
    ? `$${(d / 1000).toFixed(1)}k`
    : `$${d.toFixed(2)}`;
};
const fmt$full = (cents) => `$${((cents || 0) / 100).toLocaleString('en-US', { minimumFractionDigits: 2 })}`;
const fmtDate  = (iso)   => dayjs(iso).format('MMM D');
// Region-aware full formatters
const fmtINR = (paise) => `₹${((paise || 0) / 100).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;
const fmtUSD = (cents) => `$${((cents || 0) / 100).toLocaleString('en-US', { minimumFractionDigits: 2 })}`;
// Format an amount using the row's own currency — INR (₹) for India, USD ($) otherwise.
const fmtAmt = (amount, currency) => (String(currency || 'inr').toLowerCase() === 'inr' ? fmtINR(amount) : fmtUSD(amount));

const PRESETS = [
  { label: 'Today',   days: 0  },
  { label: '7 days',  days: 7  },
  { label: '30 days', days: 30 },
  { label: '90 days', days: 90 },
];

/* ── Small stat tile ── */
const StatCard = ({ title, value, sub, icon, color, change }) => {
  const { token: tk } = antdTheme.useToken();
  const isUp   = change > 0;
  const isDown = change < 0;
  return (
    <Card style={{ borderRadius: 12, border: `1px solid ${tk.colorBorderSecondary}`, height: '100%' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <div>
          <div style={{ fontSize: 12, color: tk.colorTextSecondary, marginBottom: 6, fontWeight: 500 }}>{title}</div>
          <div style={{ fontSize: 26, fontWeight: 700, color, lineHeight: 1.1, fontFamily: 'var(--font-mono)', letterSpacing: '-0.03em' }}>
            {value}
          </div>
          {sub && <div style={{ fontSize: 12, color: tk.colorTextTertiary, marginTop: 4 }}>{sub}</div>}
          {change != null && (
            <div style={{ fontSize: 12, fontWeight: 600, marginTop: 5, color: isUp ? 'var(--ok)' : isDown ? 'var(--danger)' : tk.colorTextSecondary }}>
              {isUp ? '▲' : isDown ? '▼' : '–'} {change != null ? `${Math.abs(change).toFixed(1)}% vs prev period` : ''}
            </div>
          )}
        </div>
        <div style={{ width: 42, height: 42, borderRadius: 10, background: `${color}18`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          {React.cloneElement(icon, { style: { fontSize: 20, color } })}
        </div>
      </div>
    </Card>
  );
};

/* ── Custom recharts tooltip ── */
const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{
      background: 'var(--surface)', border: '1px solid var(--border)',
      borderRadius: 8, padding: '8px 12px', fontSize: 12,
      boxShadow: 'var(--shadow-sm)',
    }}>
      <div style={{ fontWeight: 600, color: 'var(--text)', marginBottom: 4 }}>{label}</div>
      {payload.map(p => (
        <div key={p.name} style={{ color: p.color }}>
          {p.name}: <strong>{p.name === 'Revenue' ? fmt$full(p.value * 100) : p.value}</strong>
        </div>
      ))}
    </div>
  );
};

/* ── Main page ── */
const AdminRevenue = () => {
  const { token: tk } = antdTheme.useToken();

  const [preset,    setPreset]    = useState('30 days');
  const [customRange, setCustomRange] = useState(null);   // [dayjs, dayjs] | null
  const [data,      setData]      = useState(null);
  const [loading,   setLoading]   = useState(true);
  const [error,     setError]     = useState('');
  const [chartType, setChartType] = useState('area');

  /* resolve date range from preset or custom */
  const getRange = useCallback(() => {
    if (customRange) {
      return {
        from: customRange[0].startOf('day').toISOString(),
        to:   customRange[1].endOf('day').toISOString(),
      };
    }
    const p = PRESETS.find(p => p.label === preset);
    const to   = dayjs().endOf('day');
    const from = p?.days === 0
      ? dayjs().startOf('day')
      : dayjs().subtract(p?.days ?? 30, 'day').startOf('day');
    return { from: from.toISOString(), to: to.toISOString() };
  }, [preset, customRange]);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { from, to } = getRange();
      const res = await axios.get(`/api/admin/revenue?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`, { headers: authHeader() });
      setData(res.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load revenue data');
    }
    setLoading(false);
  }, [getRange]);

  useEffect(() => { load(); }, [load]);

  /* prepare chart series — group by week if > 30 days */
  const chartData = (() => {
    if (!data?.daily) return [];
    const days = data.daily;
    const isLong = days.length > 45;
    if (!isLong) {
      return days.map(d => ({
        date:    fmtDate(d.date),
        Revenue: parseFloat(((d.revenue || 0) / 100).toFixed(2)),
        Invoices: d.count,
      }));
    }
    // Group into weeks
    const weeks = {};
    days.forEach(d => {
      const w = dayjs(d.date).startOf('week').format('MMM D');
      if (!weeks[w]) weeks[w] = { date: w, Revenue: 0, Invoices: 0 };
      weeks[w].Revenue  = parseFloat((weeks[w].Revenue  + (d.revenue || 0) / 100).toFixed(2));
      weeks[w].Invoices += d.count;
    });
    return Object.values(weeks);
  })();

  const s = data?.summary;

  const invoiceColumns = [
    {
      title: 'Invoice', dataIndex: 'invoice_number', width: 150,
      render: v => <Text style={{ fontFamily: 'var(--font-mono)', fontSize: 12 }}>{v}</Text>,
    },
    {
      title: 'Customer', key: 'customer',
      render: (_, r) => (
        <div>
          <div style={{ fontWeight: 500, fontSize: 13 }}>{r.org_name || `Org #${r.org_id}`}</div>
          <div style={{ fontSize: 11, color: tk.colorTextTertiary }}>{r.org_email}</div>
        </div>
      ),
    },
    {
      title: 'Amount', dataIndex: 'amount', width: 110, align: 'right',
      render: (v, r) => <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 600, color: 'var(--ok)' }}>{fmtAmt(v, r.currency)}</span>,
      sorter: (a, b) => a.amount - b.amount,
    },
    {
      title: 'Status', dataIndex: 'status', width: 90,
      render: s => <Tag color={s === 'paid' ? 'green' : s === 'open' ? 'orange' : 'default'} style={{ borderRadius: 5, fontSize: 11 }}>{s}</Tag>,
    },
    {
      title: 'Date', dataIndex: 'paid_at', width: 120,
      render: d => <span style={{ fontSize: 12, color: tk.colorTextSecondary }}>{d ? dayjs(d).format('MMM D, YYYY') : '—'}</span>,
      sorter: (a, b) => new Date(a.paid_at) - new Date(b.paid_at),
      defaultSortOrder: 'descend',
    },
  ];

  const topColumns = [
    {
      title: '#', key: 'rank', width: 40,
      render: (_, __, i) => <span style={{ color: tk.colorTextTertiary, fontWeight: 600 }}>{i + 1}</span>,
    },
    {
      title: 'Customer', key: 'name',
      render: (_, r) => (
        <div>
          <div style={{ fontWeight: 500 }}>{r.org_name}</div>
          <div style={{ fontSize: 11, color: tk.colorTextTertiary }}>{r.org_email}</div>
        </div>
      ),
    },
    {
      title: 'Invoices', dataIndex: 'invoices', width: 80, align: 'center',
      render: v => <Tag style={{ borderRadius: 5, fontFamily: 'var(--font-mono)' }}>{v}</Tag>,
    },
    {
      title: 'Revenue', dataIndex: 'revenue', width: 120, align: 'right',
      render: (v, r) => <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 700, color: 'var(--ok)' }}>{fmtAmt(v, r.currency)}</span>,
    },
  ];

  return (
    <div>
      {/* ── Filter bar ── */}
      <Card
        style={{ borderRadius: 12, border: `1px solid ${tk.colorBorderSecondary}`, marginBottom: 20 }}
        styles={{ body: { padding: '12px 16px' } }}
      >
        <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
          <CalendarOutlined style={{ color: 'var(--brand-primary)', fontSize: 15 }} />
          <Segmented
            value={customRange ? 'Custom' : preset}
            onChange={v => { if (v !== 'Custom') { setCustomRange(null); setPreset(v); } }}
            options={[...PRESETS.map(p => p.label), 'Custom']}
          />
          <RangePicker
            value={customRange}
            onChange={v => { setCustomRange(v); if (v) setPreset('Custom'); }}
            disabledDate={d => d && d > dayjs()}
            format="MMM D, YYYY"
            style={{ borderRadius: 8 }}
          />
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
            <Segmented
              value={chartType}
              onChange={setChartType}
              options={[{ label: 'Area', value: 'area' }, { label: 'Bar', value: 'bar' }]}
              size="small"
            />
            <Button icon={<ReloadOutlined />} onClick={load} size="small" style={{ borderRadius: 8 }}>
              Refresh
            </Button>
          </div>
        </div>
      </Card>

      {error && <Alert type="error" message={error} showIcon closable style={{ marginBottom: 16, borderRadius: 10 }} />}

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 80 }}><Spin size="large" /></div>
      ) : (
        <>
          {/* ── Region revenue (separate currencies — never summed) ── */}
          <Row gutter={[16, 16]} style={{ marginBottom: 20 }}>
            <Col xs={12} md={6}>
              <StatCard
                title="🇮🇳 India Revenue (INR)"
                value={fmtINR(data?.byRegion?.india?.revenue)}
                sub={`${data?.byRegion?.india?.invoiceCount || 0} inv · GST ${fmtINR(data?.byRegion?.india?.taxCollected)}`}
                icon={<DollarOutlined />}
                color="#10b981"
                change={data?.byRegion?.india?.change}
              />
            </Col>
            <Col xs={12} md={6}>
              <StatCard
                title="🌍 Intl Revenue (USD)"
                value={fmtUSD(data?.byRegion?.international?.revenue)}
                sub={`${data?.byRegion?.international?.invoiceCount || 0} inv · export, no GST`}
                icon={<DollarOutlined />}
                color="#3b82f6"
                change={data?.byRegion?.international?.change}
              />
            </Col>
            <Col xs={12} md={6}>
              <StatCard
                title="Active Pro Orgs"
                value={s?.activeProOrgs?.toLocaleString()}
                sub={`${s?.invoiceCount || 0} invoices in period`}
                icon={<CrownOutlined />}
                color="#f59e0b"
              />
            </Col>
            <Col xs={12} md={6}>
              <StatCard
                title="New Pro Subs"
                value={s?.newProOrgs?.toLocaleString()}
                sub={`in selected period`}
                icon={<TeamOutlined />}
                color="#2e6bf5"
              />
            </Col>
          </Row>

          {/* ── Revenue chart ── */}
          <Card
            title={
              <span style={{ fontWeight: 700, fontSize: 14 }}>
                Revenue Over Time
                <span style={{ fontWeight: 400, fontSize: 12, color: tk.colorTextSecondary, marginLeft: 8 }}>
                  {chartData.length > 45 ? '(grouped weekly)' : '(daily)'}
                </span>
              </span>
            }
            style={{ borderRadius: 12, border: `1px solid ${tk.colorBorderSecondary}`, marginBottom: 20 }}
          >
            {chartData.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px 0', color: tk.colorTextQuaternary }}>
                No revenue data for this period
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={280}>
                {chartType === 'area' ? (
                  <AreaChart data={chartData} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                    <defs>
                      <linearGradient id="revGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%"  stopColor="#10b981" stopOpacity={0.25} />
                        <stop offset="95%" stopColor="#10b981" stopOpacity={0}    />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke={tk.colorBorderSecondary} />
                    <XAxis dataKey="date" tick={{ fontSize: 11, fill: tk.colorTextTertiary }} tickLine={false} axisLine={false} />
                    <YAxis tick={{ fontSize: 11, fill: tk.colorTextTertiary }} tickLine={false} axisLine={false} tickFormatter={v => `$${v}`} width={55} />
                    <RTooltip content={<ChartTooltip />} />
                    <Area type="monotone" dataKey="Revenue" stroke="#10b981" strokeWidth={2} fill="url(#revGrad)" dot={false} activeDot={{ r: 4 }} />
                  </AreaChart>
                ) : (
                  <BarChart data={chartData} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={tk.colorBorderSecondary} />
                    <XAxis dataKey="date" tick={{ fontSize: 11, fill: tk.colorTextTertiary }} tickLine={false} axisLine={false} />
                    <YAxis tick={{ fontSize: 11, fill: tk.colorTextTertiary }} tickLine={false} axisLine={false} tickFormatter={v => `$${v}`} width={55} />
                    <RTooltip content={<ChartTooltip />} />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Bar dataKey="Revenue" fill="#10b981" radius={[4, 4, 0, 0]} maxBarSize={48} />
                    <Bar dataKey="Invoices" fill="#0a40d9" radius={[4, 4, 0, 0]} maxBarSize={48} />
                  </BarChart>
                )}
              </ResponsiveContainer>
            )}
          </Card>

          <Row gutter={[16, 16]}>
            {/* ── Top customers ── */}
            <Col xs={24} lg={10}>
              <Card
                title={<span style={{ fontWeight: 700, fontSize: 14 }}>Top Customers</span>}
                style={{ borderRadius: 12, border: `1px solid ${tk.colorBorderSecondary}`, height: '100%' }}
              >
                <Table
                  dataSource={data?.topCustomers || []}
                  columns={topColumns}
                  rowKey="org_id"
                  pagination={false}
                  size="small"
                  locale={{ emptyText: 'No revenue in this period' }}
                
          scroll={{ x: 'max-content' }}
        />
              </Card>
            </Col>

            {/* ── Invoice list ── */}
            <Col xs={24} lg={14}>
              <Card
                title={
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontWeight: 700, fontSize: 14 }}>Recent Invoices</span>
                    <Tag style={{ borderRadius: 6, fontFamily: 'var(--font-mono)', fontSize: 11 }}>
                      {data?.invoices?.length || 0} shown
                    </Tag>
                  </div>
                }
                style={{ borderRadius: 12, border: `1px solid ${tk.colorBorderSecondary}` }}
              >
                <Table
                  dataSource={data?.invoices || []}
                  columns={invoiceColumns}
                  rowKey="id"
                  size="small"
                  pagination={{ pageSize: 10, showSizeChanger: false, showTotal: t => `${t} invoices` }}
                  locale={{ emptyText: 'No invoices in this period' }}
                  scroll={{ x: 560 }}
                />
              </Card>
            </Col>
          </Row>
        </>
      )}
    </div>
  );
};

export default AdminRevenue;
